import { ElementRef, Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthenticationApiService } from '@warema/shared/authentication/api';
import {
    AppointmentRequestActivityType,
    GoogleAction,
    SearchCoordinates,
    CompanySearchRequest,
    UserType,
    WorkflowOrigin,
} from '@warema/shared/models';
import { AppointmentTrackingService, GoogleService, SunCenterService } from '@warema/shared/services';
import { TenantConfigurationService } from '@warema/shared/tenant-config';
import { combineLatest, Observable } from 'rxjs';
import { debounceTime, filter, first, map, startWith, switchMap, take } from 'rxjs/operators';
import { AutocompleteOptions } from './models';
import { SearchDataService } from './search-data.service';
import { ActivityDataService } from '../../activity/services/activity-data.service';
import { DirectSearchTriggerService } from '../../direct-search-trigger/direct-search-trigger.service';
import { ProductCategoryDataService } from '../../product-category/services/product-category-data.service';

@Injectable({
    providedIn: 'root',
})
export class SearchService {
    public readonly googleService = inject(GoogleService);
    public readonly searchDataService = inject(SearchDataService);
    public readonly productCategoryDataService = inject(ProductCategoryDataService);
    private readonly _activityDataServie = inject(ActivityDataService);
    private readonly _authenticationApiService = inject(AuthenticationApiService);
    private readonly _trackingService = inject(AppointmentTrackingService);
    private readonly _directSearchTriggerService = inject(DirectSearchTriggerService);
    private readonly _sunCenterService = inject(SunCenterService);
    private readonly _tenantConfig = inject(TenantConfigurationService);
    private readonly _route = inject(ActivatedRoute);

    private _searchMoreRequestData$ = new Observable<CompanySearchRequest>();

    private _autocompleteOptions: AutocompleteOptions = {
        types: ['(regions)'],
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        region: this._tenantConfig.settings?.country || 'DE',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        componentRestrictions: { country: this._tenantConfig.settings?.country || 'DE' },
    };
    private _isUnrestrictedSearch = false;
    private _isRestoredSearchReady = false;
    private _isInstantOfferSearch = false;
    private _workflowOrigin: WorkflowOrigin;

    public initializeSearchLogic(
        googleAutoCompleteElement: ElementRef<HTMLInputElement>,
        isInstantOfferSearch: boolean,
        workflowOrigin: Observable<WorkflowOrigin | null>,
    ): void {
        workflowOrigin.subscribe(x => (this._workflowOrigin = x));
        this._isInstantOfferSearch = isInstantOfferSearch;
        if (
            this._sunCenterService.isSunCenter$.getValue() ||
            this._workflowOrigin === WorkflowOrigin.SunExperienceManager
        ) {
            this._setupUnrestrictedSearch();
            this._initSearchAndSearchMore(googleAutoCompleteElement);
            return;
        }

        this._authenticationApiService.authenticationComplete$
            .pipe(
                filter(complete => complete),
                take(1),
            )
            .subscribe(() => {
                if (
                    this._authenticationApiService.loggedIn$.getValue() &&
                    this._authenticationApiService.user$.getValue().userType === UserType.Employee
                ) {
                    this._setupUnrestrictedSearch();
                }

                this._initSearchAndSearchMore(googleAutoCompleteElement);
            });
    }

    private _initSearchAndSearchMore(googleAutoCompleteElement: ElementRef<HTMLInputElement>): void {
        this._listenToSearch(googleAutoCompleteElement);
        this._searchMoreRequestData$ = this._generateSearchMoreRequestData();
    }

    private _listenToSearch(googleAutoCompleteElement: ElementRef<HTMLInputElement>): void {
        if (
            this._directSearchTriggerService.isDirectSearchTriggerMode$.getValue() &&
            !this._directSearchTriggerService.initialSearchCompleted
        ) {
            this.searchDataService.clearCompanySearchDataExceptLocationAndInstantOffer();
            this._directSearchTriggerService.initializeStoreForDirectSearchTrigger();
            const address = this._directSearchTriggerService.directSearchTriggeerAddress$.getValue();
            setTimeout(() => {
                this.googleService
                    .getAddressCoordinates(address)
                    .pipe(first())
                    .subscribe(coordinates => {
                        this.searchDataService.initializeSearchCoordinatesForRegionalSea(coordinates);
                        this._initSearchBasedOnLastSearchedCoordinates(googleAutoCompleteElement);
                    });
            }, 300);
        } else {
            this._initSearchBasedOnLastSearchedCoordinates(googleAutoCompleteElement);
        }
    }

    private _initSearchBasedOnLastSearchedCoordinates(googleAutoCompleteElement: ElementRef<HTMLInputElement>): void {
        this.searchDataService.latestSearchedCoordinates$.pipe(first()).subscribe(lastCoords => {
            if (lastCoords.address === '') {
                this._initFreshSearch(googleAutoCompleteElement);
            } else {
                this._isRestoredSearchReady = false;
                this._restorePreviousSearch(googleAutoCompleteElement, lastCoords);
            }
        });
    }

    private _restorePreviousSearch(
        googleAutoCompleteElement: ElementRef<HTMLInputElement>,
        lastCoords: SearchCoordinates,
    ): void {
        combineLatest([
            this.googleService.initGoogleMapsAutoComplete(googleAutoCompleteElement, this._autocompleteOptions).pipe(
                filter(v => v != null && [GoogleAction.ENTER, GoogleAction.AUTO_COMPLETE].includes(v.event)),
                startWith({
                    address: lastCoords.address,
                    latitude: lastCoords.latitude,
                    longitude: lastCoords.longitude,
                    event: GoogleAction.ENTER,
                }),
            ),
            this.searchDataService.instantOfferValue$,
            this._activityDataServie.selectedActivity$.pipe(
                filter(activity => activity !== AppointmentRequestActivityType.Unknown),
            ),
        ])
            .pipe(
                switchMap(([locationValue, filterByDigitalBuyingAdvisorValue, activityValue]) =>
                    this.productCategoryDataService.selectedSubProductCategoriesIds$.pipe(
                        debounceTime(100), // wait to prevent productCategories being recognized too late
                        first(),
                        map(productCategoriesValue => ({
                            locationValue,
                            filterByDigitalBuyingAdvisorValue,
                            activityValue,
                            productCategoriesValue,
                        })),
                    ),
                ),
            )
            .subscribe(
                ({ locationValue, filterByDigitalBuyingAdvisorValue, activityValue, productCategoriesValue }) => {
                    if (
                        this._isRestoredSearchReady ||
                        (this._directSearchTriggerService.isDirectSearchTriggerMode$.getValue() &&
                            !this._directSearchTriggerService.initialSearchCompleted)
                    ) {
                        const consultationId = this._route.snapshot.queryParamMap.get('consultationId') ?? undefined;
                        this._trackingService.trackLocationSearch(
                            locationValue.address,
                            this._workflowOrigin,
                            consultationId,
                        );
                        googleAutoCompleteElement.nativeElement.value = locationValue.address;
                        const search = this._generateSearchRequest(
                            locationValue?.latitude,
                            locationValue?.longitude,
                            activityValue,
                            productCategoriesValue,
                            locationValue?.address,
                            filterByDigitalBuyingAdvisorValue,
                        );
                        this.searchDataService.searchCompanies(search);

                        this._directSearchTriggerService.completeDirectSearchTriggerFlow();
                        this._isRestoredSearchReady = true;
                    } else {
                        this._isRestoredSearchReady = true;
                    }
                },
            );
    }

    private _initFreshSearch(googleAutoCompleteElement: ElementRef<HTMLInputElement>): void {
        combineLatest([
            this.googleService
                .initGoogleMapsAutoComplete(googleAutoCompleteElement, this._autocompleteOptions)
                .pipe(filter(v => v != null && [GoogleAction.ENTER, GoogleAction.AUTO_COMPLETE].includes(v.event))),
            this.searchDataService.instantOfferValue$,
            this._activityDataServie.selectedActivity$.pipe(
                filter(activity => activity !== AppointmentRequestActivityType.Unknown),
            ),
        ])
            .pipe(
                switchMap(([locationValue, filterByDigitalBuyingAdvisorValue, activityValue]) =>
                    this.productCategoryDataService.selectedSubProductCategoriesIds$.pipe(
                        debounceTime(100), // wait to prevent productCategories being recognized too late
                        first(),
                        map(productCategoriesValue => ({
                            locationValue,
                            filterByDigitalBuyingAdvisorValue,
                            activityValue,
                            productCategoriesValue,
                        })),
                    ),
                ),
            )
            .subscribe(
                ({ locationValue, filterByDigitalBuyingAdvisorValue, activityValue, productCategoriesValue }) => {
                    const consultationId = this._route.snapshot.queryParamMap.get('consultationId') ?? undefined;
                    this._trackingService.trackLocationSearch(
                        locationValue.address,
                        this._workflowOrigin,
                        consultationId,
                    );
                    googleAutoCompleteElement.nativeElement.value = locationValue.address;
                    const search = this._generateSearchRequest(
                        locationValue?.latitude,
                        locationValue?.longitude,
                        activityValue,
                        productCategoriesValue,
                        locationValue?.address,
                        filterByDigitalBuyingAdvisorValue,
                    );

                    this.searchDataService.searchCompanies(search);
                },
            );
    }

    public searchMore(): void {
        this._searchMoreRequestData$.pipe(first()).subscribe(x => {
            this.searchDataService.searchMoreCompanies(x);
        });
    }

    public findMe(googleAutoCompleteElement: ElementRef<HTMLInputElement>): void {
        navigator?.geolocation?.getCurrentPosition(position => {
            this.googleService
                .getAddressByLatLong(googleAutoCompleteElement, position.coords.latitude, position.coords.longitude)
                .pipe(
                    first(),
                    switchMap(googleLocation =>
                        this._activityDataServie.selectedActivity$.pipe(
                            first(),
                            map(activity => ({ googleLocation, activity })),
                        ),
                    ),
                    switchMap(({ googleLocation, activity }) =>
                        this.productCategoryDataService.selectedSubProductCategoriesIds$.pipe(
                            debounceTime(100), // wait to prevent productCategories being recognized too late
                            first(),
                            map(productCategories => ({ googleLocation, activity, productCategories })),
                        ),
                    ),
                    switchMap(({ googleLocation, activity, productCategories }) =>
                        this.searchDataService.instantOfferValue$.pipe(
                            first(),
                            map(instantOffer => ({ googleLocation, activity, productCategories, instantOffer })),
                        ),
                    ),
                )
                .subscribe(({ googleLocation, activity, productCategories, instantOffer }) => {
                    googleAutoCompleteElement.nativeElement.value = googleLocation.address;
                    const consultationId = this._route.snapshot.queryParamMap.get('consultationId') ?? undefined;
                    this._trackingService.trackFindMe(googleLocation.address, this._workflowOrigin, consultationId);
                    const search = this._generateSearchRequest(
                        googleLocation.latitude,
                        googleLocation.longitude,
                        activity,
                        productCategories,
                        googleLocation.address,
                        instantOffer,
                    );
                    this.searchDataService.searchCompanies(search);
                });
        });
    }

    private _setupUnrestrictedSearch(): void {
        this._autocompleteOptions = { types: ['(regions)'] };
        this._isUnrestrictedSearch = true;
    }

    private _generateSearchRequest(
        latitude: number,
        longitude: number,
        activity: AppointmentRequestActivityType,
        subProductCategories: Array<number>,
        address: string,
        filterByDigitalBuyingAdvisor: boolean,
        skipCompanyIds: Array<string> = [],
    ): CompanySearchRequest {
        const isSunCenterScenario = this._sunCenterService.isSunCenter$.getValue();

        return {
            latitude,
            longitude,
            range: 20,
            maxRange: this._tenantConfig.settings.companySearch.maxSearchRadius,
            count: isSunCenterScenario ? 4 : this._tenantConfig.settings.companySearch.searchResultLength,
            maxCount: this._tenantConfig.settings.companySearch.maxSearchResultLength,
            activity:
                activity === AppointmentRequestActivityType.Unknown
                    ? AppointmentRequestActivityType.ConsultationAtHome
                    : activity,
            country: this._isUnrestrictedSearch ? undefined : this._tenantConfig.settings.country,
            searchAddress: address,
            isRegionalSea: false,
            filterByDigitalBuyingAdvisor:
                this._isInstantOfferSearch || activity === AppointmentRequestActivityType.Quotation
                    ? true
                    : filterByDigitalBuyingAdvisor,
            filterByInstantOfferSearch: this._isInstantOfferSearch,
            subProductCategories,
            skipCompanyIds,
        };
    }

    private _generateSearchMoreRequestData(): Observable<CompanySearchRequest> {
        return combineLatest([
            this.searchDataService.latestSearchedCoordinates$,
            this.productCategoryDataService.selectedSubProductCategoriesIds$,
            this._activityDataServie.selectedActivity$.pipe(
                filter(activity => activity !== AppointmentRequestActivityType.Unknown),
            ),
            this.searchDataService.instantOfferValue$,
            this.searchDataService.companies$.pipe(map(x => x.map(company => company.addressId))),
        ]).pipe(
            first(),
            map(
                ([
                    latestSearchedCoordinates,
                    productCategoriesValue,
                    activityValue,
                    filterByDigitalBuyingAdvisorValue,
                    companiesIds,
                ]) => {
                    return this._generateSearchRequest(
                        latestSearchedCoordinates.latitude,
                        latestSearchedCoordinates.longitude,
                        activityValue,
                        productCategoriesValue,
                        latestSearchedCoordinates.address,
                        filterByDigitalBuyingAdvisorValue,
                        companiesIds,
                    );
                },
            ),
        );
    }
}
